import { Injectable } from "@angular/core";
import { DatePipe } from "@angular/common";

@Injectable({ providedIn: "root" })
export class DateTimeService {
  private readonly timezoneName: string;
  private readonly datePipe: DatePipe;

  constructor() {
    this.timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.datePipe = new DatePipe("en-US");
  }

  getTimezoneName(): string {
    return this.timezoneName;
  }

  isDateOnlyString(value: string): boolean {
    if (!value) return false;
    const trimmed = value.trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  }

  isDateTimeString(value: string): boolean {
    if (!value) return false;
    const trimmed = value.trim();
    return /^\d{4}-\d{2}-\d{2}[T ]/.test(trimmed);
  }

  private isTimeZoneAware(value: string): boolean {
    return value.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(value);
  }

  toLocalDate(value: string | Date): Date | null {
    if (!value) return null;

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const trimmed = value.trim();
    if (!trimmed) return null;

    if (this.isDateOnlyString(trimmed)) {
      const d = new Date(trimmed + "T00:00:00");
      return isNaN(d.getTime()) ? null : d;
    }

    let parseable: string;
    if (this.isTimeZoneAware(trimmed)) {
      parseable = trimmed;
    } else if (this.isDateTimeString(trimmed)) {
      parseable = trimmed + "Z";
    } else {
      parseable = trimmed;
    }

    const d = new Date(parseable);
    return isNaN(d.getTime()) ? null : d;
  }

  toLocalISOString(value: string | Date): string | null {
    const local = this.toLocalDate(value);
    if (!local) return null;
    const year = local.getFullYear();
    const month = (local.getMonth() + 1).toString().padStart(2, "0");
    const day = local.getDate().toString().padStart(2, "0");
    const hours = local.getHours().toString().padStart(2, "0");
    const minutes = local.getMinutes().toString().padStart(2, "0");
    const seconds = local.getSeconds().toString().padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  formatDateOnly(value: string | Date): string | null {
    const local = this.toLocalDate(value);
    if (!local) return null;
    const year = local.getFullYear();
    const month = (local.getMonth() + 1).toString().padStart(2, "0");
    const day = local.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  format(
    value: string | Date,
    format?: string,
    timezone?: string,
  ): string | null {
    const local = this.toLocalDate(value);
    if (!local) return null;
    return this.datePipe.transform(
      local,
      format,
      timezone || this.timezoneName,
    );
  }

  timeAgo(value: string | Date): string {
    const past = this.toLocalDate(value);
    if (!past) return "";
    const now = new Date();
    const diffMs = now.getTime() - past.getTime();
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return `${seconds} sec${seconds !== 1 ? "s" : ""} ago`;
    if (minutes < 60) return `${minutes} min${minutes !== 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
    if (days < 7) return `${days} day${days !== 1 ? "s" : ""} ago`;
    if (weeks < 4) return `${weeks} week${weeks !== 1 ? "s" : ""} ago`;
    if (months < 12) return `${months} month${months !== 1 ? "s" : ""} ago`;
    return `${years} year${years !== 1 ? "s" : ""} ago`;
  }
}
